import { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";

const NETWORKS = ["Instagram", "TikTok", "Facebook"];

const FIELDS = [
  { k: "nome", label: "Nome da marca", ph: "Ex: FitGlow Studio", area: false },
  { k: "nicho", label: "Nicho", ph: "Ex: Fitness feminino", area: false },
  { k: "produto", label: "Produto ou serviço", ph: "O que você vende?", area: true },
  { k: "publico", label: "Público-alvo", ph: "Quem é seu cliente ideal?", area: true },
  { k: "objetivos", label: "Objetivos", ph: "Ex: Mais seguidores e leads", area: true },
  { k: "regiao", label: "Região", ph: "Ex: Brasil / São Paulo", area: false },
  { k: "tom_de_voz", label: "Tom de voz", ph: "Ex: Motivador e próximo", area: false },
  { k: "concorrentes", label: "Concorrentes", ph: "Principais concorrentes", area: true },
  { k: "diferenciais", label: "Diferenciais", ph: "O que te torna único?", area: true },
];

export default function BriefingForm({ brand, onSaved }) {
  const empty = { nome: "", nicho: "", produto: "", publico: "", objetivos: "", regiao: "", tom_de_voz: "", concorrentes: "", diferenciais: "", redes_sociais: ["Instagram", "TikTok", "Facebook"], instagram_handle: "", tiktok_handle: "", facebook_handle: "" };
  const [form, setForm] = useState(brand ? { ...empty, ...brand } : empty);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleNet = (n) => setForm((f) => ({
    ...f,
    redes_sociais: f.redes_sociais.includes(n) ? f.redes_sociais.filter((x) => x !== n) : [...f.redes_sociais, n],
  }));

  const submit = async () => {
    if (!form.nome.trim()) { toast.error("Informe o nome da marca."); return; }
    setSaving(true);
    try {
      const payload = { ...form };
      let res;
      if (brand?.brand_id) res = await api.put(`/brands/${brand.brand_id}`, payload);
      else res = await api.post("/brands", payload);
      toast.success(brand ? "Briefing atualizado!" : "Marca criada com sucesso!");
      onSaved?.(res.data);
    } catch (e) {
      toast.error("Erro ao salvar o briefing.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6">
      <div className="mb-6">
        <p className="overline text-emerald-400 mb-1">Etapa 1 — Briefing</p>
        <h2 className="font-display text-2xl font-bold tracking-tight">{brand ? "Editar briefing" : "Novo cliente / marca"}</h2>
        <p className="text-sm text-slate-400 mt-1">O Orquestrador coleta estas informações para delegar aos agentes.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        {FIELDS.map((f) => (
          <div key={f.k} className={f.area ? "md:col-span-2" : ""}>
            <Label className="text-slate-300 text-sm mb-1.5 block">{f.label}</Label>
            {f.area ? (
              <Textarea data-testid={`input-briefing-${f.k}`} value={form[f.k]} placeholder={f.ph}
                onChange={(e) => set(f.k, e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40 min-h-[80px]" />
            ) : (
              <Input data-testid={`input-briefing-${f.k}`} value={form[f.k]} placeholder={f.ph}
                onChange={(e) => set(f.k, e.target.value)}
                className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
            )}
          </div>
        ))}

        <div className="md:col-span-2">
          <Label className="text-slate-300 text-sm mb-2 block">Redes sociais utilizadas</Label>
          <div className="flex flex-wrap gap-4">
            {NETWORKS.map((n) => (
              <label key={n} className="flex items-center gap-2 cursor-pointer text-sm text-slate-300">
                <Checkbox data-testid={`checkbox-net-${n.toLowerCase()}`}
                  checked={form.redes_sociais.includes(n)} onCheckedChange={() => toggleNet(n)}
                  className="border-slate-600 data-[state=checked]:bg-emerald-500 data-[state=checked]:border-emerald-500" />
                {n}
              </label>
            ))}
          </div>
        </div>

        <div className="md:col-span-2 grid sm:grid-cols-3 gap-4">
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">@ Instagram</Label>
            <Input data-testid="input-briefing-instagram_handle" value={form.instagram_handle} placeholder="@suamarca"
              onChange={(e) => set("instagram_handle", e.target.value)}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">@ TikTok</Label>
            <Input data-testid="input-briefing-tiktok_handle" value={form.tiktok_handle} placeholder="@suamarca"
              onChange={(e) => set("tiktok_handle", e.target.value)}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
          <div>
            <Label className="text-slate-300 text-sm mb-1.5 block">@ Facebook</Label>
            <Input data-testid="input-briefing-facebook_handle" value={form.facebook_handle} placeholder="página/usuário"
              onChange={(e) => set("facebook_handle", e.target.value)}
              className="bg-slate-900/60 border-slate-700 focus-visible:ring-emerald-500/40" />
          </div>
        </div>
      </div>

      <div className="mt-6 flex justify-end">
        <Button data-testid="btn-save-briefing" onClick={submit} disabled={saving}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold hover:brightness-110 transition-all">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          {brand ? "Salvar alterações" : "Criar marca"}
        </Button>
      </div>
    </div>
  );
}
